import { decodeToken } from "react-jwt";
import type { ReadonlyDeep } from "type-fest";
import Cookies from "universal-cookie";

import type { DataGroupEntity } from "src/app/features/DataGroup/types/DataGroupEntity";
import type { CredentialsDto } from "../../types/be/generated";
import { isDevelopment } from "../../utils/appVersion";
import type { AuthToken, RefreshToken } from "./types";

export enum StorageEvents {
    DATA_GROUP_CHANGED = "DataGroupChanged",
}

type JWToken = {
    oid: string;
    exp: number;
    given_name: string;
    unique_name: string;
    iat: number;
    id: number;
    jti: number;
    sub: string;
    tokenType: string;
};

const cookies = new Cookies();

const APA_ACCESS_TOKEN = "jwtApaAccessToken";
const APA_ID_TOKEN = "jwtApaIdToken";
const IDM_REFRESH_TOKEN = "idmRefreshToken";
const SELECTED_DATA_GROUP = "selectedDataGroup";
const USER_RIGHTS = "userRights";

// eslint-disable-next-line no-underscore-dangle
const isMSEntraLogin = window.__RUNTIME_CONFIG__?.IDENTITY_SERVICE === "entra";

export const setAccessToken = (accessToken: AuthToken | null) => {
    if (accessToken) {
        const options = isDevelopment() ? {} : { secure: true, sameSite: "strict" as "strict", path: "/" };
        cookies.set(APA_ACCESS_TOKEN, accessToken, options);
    } else {
        deleteCookieAccessToken();
    }
};

export const getAccessToken = (): AuthToken | null => {
    return cookies.get(APA_ACCESS_TOKEN);
};

export const setIdToken = (idToken: AuthToken | null) => {
    if (idToken) {
        const options = isDevelopment() ? {} : { secure: true, sameSite: "strict" as "strict", path: "/" };
        cookies.set(APA_ID_TOKEN, idToken, options);
    } else {
        deleteCookieIdToken();
    }
};

export const getIdToken = (): AuthToken | null => {
    return cookies.get(APA_ID_TOKEN);
};

export const deleteCookieAccessToken = () => {
    cookies.remove(APA_ACCESS_TOKEN);
};

export const deleteCookieIdToken = () => {
    cookies.remove(APA_ID_TOKEN);
};

export const setUserRights = (userRights: ReadonlyDeep<CredentialsDto["userRights"]>) => {
    if (userRights) {
        const toStringUserRights: string = userRights.join(",");
        window.localStorage.setItem(USER_RIGHTS, toStringUserRights);
    }
};

export const getUserRights = (): string[] | null => {
    const userRights = window.localStorage.getItem(USER_RIGHTS);
    if (userRights) {
        return userRights.split(",");
    }
    return null;
};

export const deleteUserRights = () => {
    window.localStorage.removeItem(USER_RIGHTS);
};

export const setRefreshToken = (refreshToken: AuthToken | null) => {
    if (refreshToken) {
        window.localStorage.setItem(IDM_REFRESH_TOKEN, refreshToken);
    } else {
        deleteRefreshToken();
    }
};

export const getRefreshToken = (): RefreshToken | null => {
    return window.localStorage.getItem(IDM_REFRESH_TOKEN);
};

export const deleteRefreshToken = () => {
    window.localStorage.removeItem(IDM_REFRESH_TOKEN);
};

export const setAssignedDataGroup = (selectedDataGroup: DataGroupEntity | null) => {
    if (selectedDataGroup == null) {
        deleteAssignedDataGroup();
    } else {
        const value = JSON.stringify(selectedDataGroup);
        window.localStorage.setItem(SELECTED_DATA_GROUP, value);
    }

    window.dispatchEvent(new Event(StorageEvents.DATA_GROUP_CHANGED));
};

export const getAssignedDataGroup = (): DataGroupEntity => {
    const selectedDataGroup = window.localStorage.getItem(SELECTED_DATA_GROUP);
    // null value is required by NoDataGroupModal
    // @ts-ignore
    return selectedDataGroup ? (JSON.parse(selectedDataGroup) as DataGroupEntity) : null;
};

export const deleteAssignedDataGroup = () => {
    window.localStorage.removeItem(SELECTED_DATA_GROUP);
};

export const getUserNameFromToken = (): string | null => {
    return getTokenInfo("given_name");
};

export const getEmailFromToken = (): string | null => {
    return getTokenInfo("unique_name");
};

export const getUserIdFromToken = (): string | number | null => {
    return isMSEntraLogin ? getTokenInfo("oid") : getTokenInfo("id");
};

const getToken = () => {
    const token = getAccessToken();
    return token ? decodeToken<JWToken>(token) : undefined;
};

// overloads for getTokenInfo
function getTokenInfo(prop: "given_name"): string | null;
function getTokenInfo(prop: "unique_name"): string | null;
function getTokenInfo(prop: "oid"): string | null;
function getTokenInfo(prop: "id"): number | null;

function getTokenInfo(prop: keyof JWToken): string | number | null {
    const token = getToken();
    if (token && prop in token) {
        return token[prop];
    }
    return null;
}
